import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

/**
 * Drill-down through the product classification: category groups (segments) -> categories -> subcategories.
 * The first two levels are navigation - picking one scopes the search to it - while subcategories are ticked as a
 * multi-select. Emits the whole scope on every change so the caller can just re-run its search.
 */
@Component({
	selector: 'app-category-tree',
	templateUrl: './category-tree.component.html',
	styleUrls: ['./category-tree.component.scss']
})
export class CategoryTreeComponent implements OnChanges {

	@Input() segments: any[] = [];
	@Input() totalCategories = 0;

	/**
	 * The scope the caller currently holds, as { segment, category, subcategories }. The tree mirrors it rather
	 * than owning its position, so a drill-down survives the component being torn down and rebuilt - which is what
	 * happens when you switch to a tab without a tree and come back.
	 */
	@Input() scope = null;

	@Output() scopeChanged: EventEmitter<any> = new EventEmitter();

	segment = null;
	category = null;
	subcategories: string[] = [];
	subcategoryFilter = '';

	/**
	 * Colours follow the `account_type` palette so a group reads the same here as everywhere else in the app - Pro AV,
	 * Residential AV and Security keep their exact account_type colour, the rest take the remaining shades.
	 */
	segmentColors = [
		{ segment: 'Pro AV', color: 'info' },
		{ segment: 'Residential AV', color: 'primary' },
		{ segment: 'Security & Fire', color: 'success' },
		{ segment: 'IT & MSPs', color: 'purple' },
		{ segment: 'Other', color: 'warning' }
	];

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.scope || changes.segments) this.applyScope();
	}

	/**
	 * Point the tree at whatever the caller's scope says. Silent by design - the caller already knows, and
	 * re-emitting here would loop.
	 */
	private applyScope(): void {
		const scope = this.scope;

		if (!scope || !scope.segment) {
			this.segment = null;
			this.category = null;
			this.subcategories = [];
			this.subcategoryFilter = '';
			return;
		}

		this.segment = (this.segments || []).find(s => s.segment === scope.segment) || null;
		this.category = this.segment
			? (this.segment.categories || []).find(c => c.category === scope.category) || null
			: null;
		this.subcategories = [...(scope.subcategories || [])];
	}

	get level() {
		if (this.category) return 'subcategories';
		if (this.segment) return 'categories';
		return 'segments';
	}

	get visibleSubcategories() {
		const term = this.subcategoryFilter.trim().toLowerCase();
		const list = this.category?.subcategories || [];
		if (!term) return list;
		return list.filter(s => (s.subcategory || '').toLowerCase().includes(term));
	}

	segmentColor(segment) {
		const match = this.segmentColors.find(s => s.segment === segment);
		return match ? match.color : 'secondary';
	}

	openSegment(segment) {
		this.segment = segment;
		this.category = null;
		this.subcategories = [];
		this.emit();
	}

	openCategory(category) {
		this.category = category;
		this.subcategories = [];
		this.subcategoryFilter = '';
		this.emit();
	}

	back() {
		if (this.category) {
			this.category = null;
			this.subcategories = [];
			this.subcategoryFilter = '';
		} else {
			this.segment = null;
		}
		this.emit();
	}

	isSubcategorySelected(subcategory) {
		return this.subcategories.indexOf(subcategory.subcategory) !== -1;
	}

	toggleSubcategory(subcategory) {
		const index = this.subcategories.indexOf(subcategory.subcategory);
		if (index === -1) {
			this.subcategories.push(subcategory.subcategory);
		} else {
			this.subcategories.splice(index, 1);
		}
		this.emit();
	}

	clear() {
		this.segment = null;
		this.category = null;
		this.subcategories = [];
		this.subcategoryFilter = '';
		this.emit();
	}

	private emit() {
		this.scopeChanged.emit({
			segment: this.segment ? this.segment.segment : null,
			category: this.category ? this.category.category : null,
			subcategories: [...this.subcategories]
		});
	}

}
